<template>
  <BaseModal
    id="sales-status-modal"
    :show="show"
    title="Último Registro de Ventas"
    size="xl"
    :processing="salesStatusLoading"
    :show-cancel="false"
    :show-confirm="false"
    @update:show="val => { $emit('update:show', val); if (!val) $emit('hide') }"
    @hidden="resetModalData"
  >
    <template #default>
      <div
        v-if="!salesStatusLoading && connectionIssues.length"
        class="credentials-alert mb-4"
      >
        <div class="credentials-alert-head">
          <i class="fa fa-key credentials-alert-icon" />
          <span class="credentials-alert-title">{{ credentialsAlertTitle }}</span>
        </div>
        <ul class="credentials-alert-list">
          <li
            v-for="issue in connectionIssues"
            :key="issue.id"
            class="credentials-alert-item"
          >
            <span class="credentials-alert-retail">{{ issue.retailName }}</span>
            <span class="credentials-alert-status">{{ issue.connectionStatus }}</span>
          </li>
        </ul>
        <p class="credentials-alert-foot">
          Mientras no se corrijan, estas cadenas dejan de cargar ventas nuevas.
        </p>
      </div>
      <div class="modern-card mb-4">
        <div class="modern-card-header" />
        <div class="modern-card-body table-container">
          <b-table
            :items="normalizedSalesStatus"
            :fields="salesStatusFields"
            class="modern-table"
            thead-class="modern-thead"
            tbody-class="modern-tbody"
            :tbody-tr-class="rowClass"
            responsive
            show-empty
            :empty-text="'Sin datos para mostrar'"
            :busy="salesStatusLoading"
          >
            <template #cell(connectionStatus)="data">
              <b-badge :variant="data.value === 'OK' ? 'success' : 'danger'">
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(incidentDescription)="data">
              <template v-if="data.value">
                <span class="incident-dot" />
                {{ data.value }}
              </template>
            </template>
            <template #table-busy>
              <div class="skeleton-wrapper">
                <div
                  v-for="n in 5"
                  :key="n"
                  class="skeleton-row"
                >
                  <div
                    v-for="field in salesStatusFields"
                    :key="field.key"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-content" />
                  </div>
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <b-button
          v-if="isAdmin"
          variant="outline-primary"
          class="action-btn mb-2 mr-2"
          @click="goToB2BCredentials"
        >
          <i class="fa fa-key mr-2" />
          Cambiar contraseñas b2b
        </b-button>
        <b-button
          v-if="isAdmin"
          variant="outline-primary"
          class="action-btn mb-2 mr-2"
          @click="goToProductMaster"
        >
          <i class="fa fa-link mr-2" />
          Mapear Productos
        </b-button>
        <b-button
          v-if="userRole !== 'Partner'"
          variant="outline-primary"
          class="action-btn mb-2"
          @click="goToLoadStatus"
        >
          <i class="fa fa-eye mr-2" />
          Ver estado de carga general
        </b-button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { sales } from '@/services/api/ExpertoRetail'
import BaseModal from '@/app/shared/components/BaseModal.vue'
import SalesStatus from '@/app/shared/models/SalesStatus.js'
import { BTable } from 'bootstrap-vue'

export default {
  name: 'SalesStatusModal',
  components: {
    BaseModal,
    BTable
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      salesStatusLoading: false,
      salesStatus: null,
      salesStatusFields: [
        { key: 'retailName', label: 'Cadena', class: 'retail-col' },
        { key: 'formattedLastSales', label: 'Última venta', class: 'last-sale-col' },
        { key: 'connectionStatus', label: 'Estado conexión', tdClass: (row) => row.connectionStatusClass, class: 'connection-col' },
        { key: 'unmappedProductsCount', label: 'Productos no mapeados', class: 'unmapped-products-col' },
        { key: 'incidentDescription', label: 'Incidencias', class: 'incidents-col' }
      ]
    }
  },
  computed: {
    userRole () {
      return this.$store.getters.userInfo?.role || null
    },
    userInfo () {
      return this.$store.getters.userInfo || {}
    },
    isAdmin () {
      return this.$store.getters.isAdmin
    },
    normalizedSalesStatus () {
      if (!this.salesStatus) return []
      // Las cadenas con credenciales caídas van primero: son las accionables y
      // antes quedaban escondidas bajo el scroll de la tabla.
      return SalesStatus.fromArray(this.salesStatus)
        .sort((a, b) => Number(b.isConnectionError) - Number(a.isConnectionError))
    },
    connectionIssues () {
      return this.normalizedSalesStatus.filter(status => status.isConnectionError)
    },
    credentialsAlertTitle () {
      const count = this.connectionIssues.length
      return count === 1
        ? '1 cadena con credenciales por corregir'
        : `${count} cadenas con credenciales por corregir`
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.getSalesStatus()
      }
    }
  },
  methods: {
    rowClass (item) {
      return item && item.isConnectionError ? 'connection-error-row' : null
    },
    resetModalData () {
      this.salesStatus = null
      this.$emit('reset')
    },
    async getSalesStatus () {
      this.salesStatusLoading = true
      try {
        this.salesStatus = await sales.salesStatus()
      } catch (error) {
        console.error(error)
      }
      this.salesStatusLoading = false
    },
    goToB2BCredentials () {
      this.$router.push({ name: 'B2BCredentialUpdate' })
      this.$emit('hide')
    },
    goToProductMaster () {
      this.$router.push({ name: 'ProductMaster' })
      this.$emit('hide')
    },
    goToLoadStatus () {
      this.$router.push({ name: 'loadStatus' })
      this.$emit('hide')
    }
  }
}
</script>

<style scoped>
.skeleton-wrapper {
  padding: 1rem 0;
}

.skeleton-row {
  display: flex;
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.skeleton-cell {
  flex: 1;
  padding: 0 0.5rem;
}

.skeleton-content {
  height: 1.2rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.table-loading {
  text-align: center;
  padding: 2rem 0;
  color: #4a5568;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.modern-table {
  background: white;
  border: none;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
}

.modern-table >>> .modern-thead th {
  background: #f7fafc;
  border: none;
  color: #4a5568;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  padding: 1rem 0.75rem;
}

.modern-table >>> .modern-tbody td {
  border: none;
  padding: 1rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #e2e8f0;
}

.modern-table >>> .modern-tbody tr:hover {
  background: #f7fafc;
}

.table-container {
  max-height: 60vh;
  overflow-y: auto;
}

.table-container::-webkit-scrollbar {
  width: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Keep table header visible while scrolling */
.modern-thead {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.credentials-alert {
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-left: 4px solid #dc3545;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.credentials-alert-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.credentials-alert-icon {
  color: #dc3545;
}

.credentials-alert-title {
  font-weight: 600;
  color: #742a2a;
}

.credentials-alert-list {
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.credentials-alert-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #feb2b2;
  border-radius: 20px;
  padding: 0.35rem 0.9rem;
}

.credentials-alert-retail {
  font-weight: 600;
  color: #2d3748;
}

.credentials-alert-status {
  font-size: 0.8rem;
  color: #c53030;
}

.credentials-alert-foot {
  margin: 0.75rem 0 0;
  font-size: 0.85rem;
  color: #742a2a;
}

.modern-table >>> .connection-error-row {
  background: #fff5f5;
}

.modern-table >>> .connection-error-row:hover {
  background: #fed7d7;
}

.incident-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #dc3545;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(220, 53, 69, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.unmapped-products-col {
  width: 20%;
}

.incidents-col {
  width: 35%;
}

/* Ensure table takes full width */
.modern-table {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/app/nav_bar/components/SalesStatusModal.vue